import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, X } from "lucide-react";
import Wordmark from "./Wordmark";
import { Reveal, EASE } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    body: "Vivoré respects your privacy. Information submitted through this site — such as your email address when joining the VIVORÉ list, or your shipping details when completing an order — is used only to fulfil your request and is never sold to third parties. Orders and accounts are handled through our secure Shopify checkout, where card details are processed by Shopify and never touch our servers. For any privacy question, contact info@vivore.com.",
  },
  terms: {
    title: "Terms of Service",
    body: "By using this website you agree to use its content for personal, non-commercial purposes. Product information is provided for reference; always read the label before use. Orders placed through this site are completed on our secure Shopify checkout and are subject to the terms shown at checkout. Vivoré™ is a trademark of the Vivoré brand. External use only; avoid contact with eyes; keep out of reach of children.",
  },
  shipping: {
    title: "Shipping & Returns",
    body: "Free standard shipping is included on every US order. Orders are picked, packed, and shipped quickly by our fulfillment partner, Omnicore 3PL, with delivery estimates shown at checkout. Returns are accepted within 30 days for a refund or replacement; refunds are usually issued within 24 hours once the return is processed.",
  },
  faq: {
    title: "FAQ",
    body: "What is Luxe Hydrating Cream? A rich yet lightweight daily body moisturizer for dry skin, with ceramides, peptides, hyaluronic acid, Manuka honey and shea butter. Is it scented? It carries a soft mango, peach and apricot scent with no synthetic fragrance. Where can I buy it? Right here on our shop page, plus on Amazon and TikTok Shop. For other questions, email info@vivore.com.",
  },
};

const COL1 = [
  { label: "Shop", id: "shop" },
  { label: "Our Story", id: "story" },
  { label: "Journal", id: "journal" },
];

export default function Footer() {
  const [legal, setLegal] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const goHomeSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 750);
    } else {
      scrollToId(id);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="relative bg-ink text-ivory overflow-hidden grain"
    >
      <div className="mx-auto max-w-[1300px] px-6 md:px-12 pt-20 md:pt-24 pb-8">
        <Reveal>
          <div className="grid grid-cols-2 md:grid-cols-[1.3fr_1fr_1.2fr_1fr] gap-10 md:gap-8 pb-16">
            <div className="col-span-2 md:col-span-1">
              <Wordmark tone="cream" className="h-9 md:h-10" />
              <p className="mt-5 font-sans text-[10px] uppercase tracking-widest2 text-ivory/40">
                Luxe Beauty, Timeless Grace
              </p>
            </div>
            <div>
              <ul className="space-y-3.5">
                {COL1.map((l) => (
                  <li key={l.id}>
                    <button
                      data-testid={`footer-link-${l.id}`}
                      onClick={() => goHomeSection(l.id)}
                      className="hover-line font-light text-sm text-ivory/70 hover:text-ivory transition-colors duration-500"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    data-testid="footer-link-account"
                    onClick={() => navigate("/account")}
                    className="hover-line font-light text-sm text-ivory/70 hover:text-ivory transition-colors duration-500"
                  >
                    Account
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <ul className="space-y-3.5">
                <li>
                  <a
                    data-testid="footer-link-contact"
                    href="mailto:info@vivore.com"
                    className="hover-line font-light text-sm text-ivory/70 hover:text-ivory transition-colors duration-500"
                  >
                    Contact Us
                  </a>
                </li>
                {["faq", "shipping", "privacy", "terms"].map((k) => (
                  <li key={k}>
                    <button
                      data-testid={`footer-legal-${k}`}
                      onClick={() => setLegal(LEGAL[k])}
                      className="hover-line font-light text-sm text-ivory/70 hover:text-ivory transition-colors duration-500"
                    >
                      {LEGAL[k].title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-sans text-sm text-ivory/70 mb-5">Follow Us</p>
              <div className="flex gap-3">
                <a
                  data-testid="footer-instagram-link"
                  href="#"
                  aria-label="Instagram"
                  onClick={(e) => e.preventDefault()}
                  className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center text-ivory/70 hover:text-ink hover:bg-ivory transition-all duration-500"
                >
                  <Instagram size={15} strokeWidth={1.5} />
                </a>
                <a
                  data-testid="footer-facebook-link"
                  href="#"
                  aria-label="Facebook"
                  onClick={(e) => e.preventDefault()}
                  className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center text-ivory/70 hover:text-ink hover:bg-ivory transition-all duration-500"
                >
                  <Facebook size={15} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="pt-7 border-t border-ivory/10 text-center">
          <p className="font-sans text-[11px] text-ivory/40 tracking-wide">
            © 2025 Vivoré. All Rights Reserved.
          </p>
        </div>
      </div>

      {legal && (
        <div
          data-testid="legal-dialog"
          className="fixed inset-0 z-[80] flex items-center justify-center p-6"
        >
          <div
            className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
            onClick={() => setLegal(null)}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative bg-[#F8F5F2] text-ink rounded-[28px] max-w-lg w-full p-9 md:p-11"
          >
            <button
              data-testid="legal-dialog-close"
              onClick={() => setLegal(null)}
              className="absolute top-6 right-6 text-ink/50 hover:text-ink transition-colors"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
            <h4 className="font-display text-3xl">{legal.title}</h4>
            <p className="mt-5 font-light text-sm leading-relaxed text-ink/70">
              {legal.body}
            </p>
          </motion.div>
        </div>
      )}
    </footer>
  );
}
